// Client-side renderer for the HTML fragments that used to be Bottle
// SimpleTemplate files (views/*.tpl). It supports what the tab templates
// actually used, so a .tpl moves into src/views/ with only its `%` lines rewritten:
//
//   {{T('text')}}  {{T("text")}}      translated, HTML-escaped
//   {{!T('text')}}                    translated, raw (the template's own HTML)
//   {{name}}  {{a.b.c}}               context value, HTML-escaped
//   {{!name}}                         context value, raw
//   {{#if name}} ... {{else}} ... {{/if}}        (was %if name: / %else: / %end)
//   {{#unless name}} ... {{/unless}}
//   {{#each list}} {{this}} {{this.x}} {{@index}} {{/each}}   (was %for x in list:)
//
// Conditions are plain context paths, never expressions: a template that used
// `%if userlevel >= 60:` gets a boolean from its view module or its JSON route
// (managerLevel) and tests that. Values come from the merged context: the
// session (user, org, isCellbieAdmin, ...) plus whatever the caller passes.
import { T } from './i18n.js';

const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
export function escapeHtml(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ESC[c]);
}

// Undo Python string-literal escapes inside a T('...') argument.
function unquote(body) {
  return body.replace(/\\(['"\\])/g, '$1');
}

function lookup(scopes, path) {
  if (path === 'this') return scopes[scopes.length - 1].this;
  if (path === '@index') return scopes[scopes.length - 1]['@index'];
  if (path === '@key') return scopes[scopes.length - 1]['@key'];
  if (path === '@first') return scopes[scopes.length - 1]['@index'] === 0;
  const parts = path.split('.');
  // innermost scope first, so {{this.x}} and loop variables shadow outer names
  for (let s = scopes.length - 1; s >= 0; s--) {
    let cur = scopes[s];
    if (cur == null || !(parts[0] in Object(cur))) continue;
    for (const part of parts) {
      if (cur == null) return undefined;
      cur = cur[part];
    }
    return cur;
  }
  return undefined;
}

const TOKEN_RE = /\{\{(!?)\s*T\(\s*(['"])((?:\\.|(?!\2).)*)\2\s*\)\s*\}\}|\{\{\s*(#if|#unless|#each)\s+([@\w.]+)\s*\}\}|\{\{\s*(else)\s*\}\}|\{\{\s*(\/if|\/unless|\/each)\s*\}\}|\{\{(!?)\s*([@A-Za-z_][\w.]*)\s*\}\}/g;

// Parse into a tree once per template string (cached). Children after an
// {{else}} land in the block's elseChildren.
const cache = new Map();

function pushChild(node, child) {
  if (node.inElse) node.elseChildren.push(child);
  else node.children.push(child);
}

function parse(html) {
  if (cache.has(html)) return cache.get(html);
  const root = { type: 'block', children: [], elseChildren: null };
  const stack = [root];
  let pos = 0;
  let m;
  TOKEN_RE.lastIndex = 0;
  while ((m = TOKEN_RE.exec(html))) {
    const top = stack[stack.length - 1];
    if (m.index > pos) pushChild(top, { type: 'text', text: html.slice(pos, m.index) });
    pos = m.index + m[0].length;
    if (m[3] !== undefined) {
      pushChild(top, { type: 't', raw: m[1] === '!', text: unquote(m[3]) });
    } else if (m[4]) {
      const node = { type: m[4].slice(1), path: m[5], children: [], elseChildren: null, inElse: false };
      pushChild(top, node);
      stack.push(node);
    } else if (m[6]) {
      if ((top.type === 'if' || top.type === 'unless') && !top.inElse) {
        top.elseChildren = [];
        top.inElse = true;
      } else {
        pushChild(top, { type: 'text', text: m[0] });
      }
    } else if (m[7]) {
      if (stack.length > 1) stack.pop();
      else pushChild(top, { type: 'text', text: m[0] });
    } else {
      pushChild(top, { type: 'var', raw: m[8] === '!', path: m[9], src: m[0] });
    }
  }
  if (pos < html.length) pushChild(root, { type: 'text', text: html.slice(pos) });
  cache.set(html, root);
  return root;
}

function renderNodes(nodes, scopes, out) {
  for (const n of nodes) {
    switch (n.type) {
      case 'text':
        out.push(n.text);
        break;
      case 't': {
        const v = T(n.text);
        out.push(n.raw ? v : escapeHtml(v));
        break;
      }
      case 'var': {
        const v = lookup(scopes, n.path);
        if (v === undefined) out.push(n.src); // leave unknown placeholders visible for debugging
        else out.push(n.raw ? String(v) : escapeHtml(v));
        break;
      }
      case 'if':
      case 'unless': {
        let truthy = !!lookup(scopes, n.path);
        if (Array.isArray(lookup(scopes, n.path))) truthy = lookup(scopes, n.path).length > 0;
        if (n.type === 'unless') truthy = !truthy;
        if (truthy) renderNodes(n.children, scopes, out);
        else if (n.elseChildren) renderNodes(n.elseChildren, scopes, out);
        break;
      }
      case 'each': {
        const list = lookup(scopes, n.path);
        if (Array.isArray(list)) {
          list.forEach((item, i) => {
            renderNodes(n.children, scopes.concat([{ this: item, '@index': i }]), out);
          });
        } else if (list && typeof list === 'object') {
          Object.keys(list).forEach((key, i) => {
            renderNodes(n.children, scopes.concat([{ this: list[key], '@index': i, '@key': key }]), out);
          });
        }
        break;
      }
      default:
        break;
    }
  }
}

export function render(html, ctx = {}) {
  const out = [];
  renderNodes(parse(html).children, [ctx], out);
  return out.join('');
}

// Render into a container and apply data-wb-if="key" / data-wb-unless="key".
export function mount(el, html, ctx = {}) {
  el.innerHTML = render(html, ctx);
  for (const node of el.querySelectorAll('[data-wb-if]')) {
    if (!lookup([ctx], node.getAttribute('data-wb-if'))) node.remove();
  }
  for (const node of el.querySelectorAll('[data-wb-unless]')) {
    if (lookup([ctx], node.getAttribute('data-wb-unless'))) node.remove();
  }
  return el;
}
